import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import { toast } from "sonner";
import { Gavel, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/auth_/reset")({
  head: () => ({
    meta: [
      { title: "Reset password — BidNova Auctions" },
      { name: "description", content: "Choose a new password for your BidNova Auctions account." },
      { name: "robots", content: "noindex, nofollow" },
    ],
    links: [{ rel: "canonical", href: "https://bidnovaauctions.uk/auth/reset" }],
  }),
  component: ResetPasswordPage,
});

const schema = z
  .object({
    password: z.string().min(8, "At least 8 characters"),
    confirm: z.string().min(1, "Confirm your password"),
  })
  .refine((d) => d.password === d.confirm, {
    message: "Passwords do not match",
    path: ["confirm"],
  });

type Status = "checking" | "ready" | "invalid" | "saving" | "done";

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>("checking");

  useEffect(() => {
    let mounted = true;

    // supabase-js parses the recovery hash automatically and fires
    // PASSWORD_RECOVERY when the link is opened. It may also arrive as an
    // already-established session on refresh.
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;
      if (event === "PASSWORD_RECOVERY" || (event === "SIGNED_IN" && session)) {
        setStatus("ready");
      }
    });

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setStatus((prev) => (prev === "ready" ? prev : data.session ? "ready" : "invalid"));
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const parsed = schema.safeParse({
      password: form.get("password"),
      confirm: form.get("confirm"),
    });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0].message);
      return;
    }
    setStatus("saving");
    const { error } = await supabase.auth.updateUser({ password: parsed.data.password });
    if (error) {
      setStatus("ready");
      toast.error(error.message);
      return;
    }
    // Force a fresh sign-in with the new password.
    await supabase.auth.signOut();
    setStatus("done");
    toast.success("Password updated — please sign in with your new password");
    navigate({ to: "/auth", search: { mode: "signin" }, replace: true });
  };

  return (
    <div className="relative min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12 overflow-hidden">
      <div
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          background:
            "radial-gradient(ellipse 60% 50% at 50% 0%, oklch(0.72 0.14 82 / 25%), transparent 70%)",
        }}
      />
      <div className="relative w-full max-w-md">
        <Link to="/" className="mb-6 flex items-center justify-center gap-2">
          <Gavel className="h-5 w-5 text-gold" />
          <span className="font-display text-xl">
            <span className="text-gold-gradient font-semibold">Elite</span> Pokémon Auctions
          </span>
        </Link>
        <div className="rounded-2xl border border-border/60 bg-card/80 backdrop-blur p-8 shadow-elegant gold-ring">
          <h1 className="font-display text-2xl mb-2">Reset your password</h1>
          <p className="text-sm text-muted-foreground mb-6">
            Choose a new password for your account. You'll be signed in again afterwards.
          </p>

          {status === "checking" && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
              <Loader2 className="h-4 w-4 animate-spin" /> Verifying reset link…
            </div>
          )}

          {status === "invalid" && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                This reset link is invalid or has expired. Please request a new password reset
                email from the sign-in page.
              </p>
              <Button asChild className="w-full bg-gold-gradient hover:opacity-90">
                <Link to="/auth" search={{ mode: "signin" }}>Back to sign in</Link>
              </Button>
            </div>
          )}

          {(status === "ready" || status === "saving" || status === "done") && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">New password</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  disabled={status !== "ready"}
                />
                <p className="text-xs text-muted-foreground">At least 8 characters.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm">Confirm new password</Label>
                <Input
                  id="confirm"
                  name="confirm"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  disabled={status !== "ready"}
                />
              </div>
              <Button
                type="submit"
                disabled={status !== "ready"}
                className="w-full bg-gold-gradient hover:opacity-90"
              >
                {status === "saving" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Update password"
                )}
              </Button>
              <p className="text-center text-xs text-muted-foreground">
                Remembered it?{" "}
                <Link to="/auth" search={{ mode: "signin" }} className="text-gold hover:underline">
                  Back to sign in
                </Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
